import { useEffect, useState, useRef } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { Layout } from "../components/Navbar";
import { Avatar } from "../components/Posts";
import { api, getUser, canClinical } from "../api";

// Pagina Follower dedicata (nel normale flusso del documento), privata.
// Accessibile SOLO al titolare autenticato del proprio profilo (pro o struttura).
// kind: "pro" -> pulsante "Bio sanitaria" (/bio/{id}); "org" -> pulsante "Bio" (/org-bio/{id}).
export default function FollowersList({ kind }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const u = getUser();
  const [list, setList] = useState(null);
  const [count, setCount] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");
  const reqRef = useRef(0);
  const expectedRole = kind === "org" ? "organization" : "professional";
  const authorized = !!u && u.role === expectedRole && u.id === id;
  useEffect(() => {
    if (!authorized) return;
    // Debounce ~280ms; il filtro è eseguito lato backend che restituisce solo id/nickname/avatar
    // (mai nome/cognome/CF/città). Il conteggio resta il totale reale dei follower.
    const handle = setTimeout(() => {
      const myId = ++reqRef.current;
      const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
      api.get(`/me/followers${qs}`)
        .then(({ data }) => { if (myId !== reqRef.current) return; setList(data.followers); setCount(data.count); })
        .catch(() => { if (myId !== reqRef.current) return; setErr(true); setList([]); });
    }, 280);
    return () => clearTimeout(handle);
  }, [authorized, q]);

  if (!u) return <Navigate to="/login" replace />;
  if (!authorized) return <Navigate to="/" replace />;

  const backTo = kind === "org" ? `/organizations/${id}` : `/professionals/${id}`;
  // Distinzione CON/SENZA attività clinica (classificazione esistente canClinical): solo la UI cambia, nessun nuovo permesso.
  const clinical = canClinical(u);
  const bioBase = clinical ? "/bio" : "/org-bio";  // clinico -> Bio sanitaria (/bio); non clinico -> Bio ordinaria (/org-bio)
  const bioLabel = clinical ? t("followx.health_bio") : t("followx.bio");

  return (<Layout><div className="yp-container max-w-2xl py-8" data-testid="followers-page">
    <Link to={backTo} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4" data-testid="followers-back">
      <ArrowLeft size={16} aria-hidden="true" /> {t("followx.back_to_profile")}
    </Link>
    <div className="flex items-baseline gap-2 mb-6">
      <h1 className="text-3xl font-bold">{t("followx.followers")}</h1>
      {count != null && <span className="text-lg text-slate-500" data-testid="followers-count">({count})</span>}
    </div>
    <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
      placeholder={t("followx.search_placeholder")} aria-label={t("followx.search_placeholder")}
      className="yp-input w-full mb-4" data-testid="followers-search" />
    {list === null && !err && <div className="text-slate-500" data-testid="followers-loading">{t("common.loading")}</div>}
    {err && <div className="text-red-600" data-testid="followers-error">{t("followx.load_error")}</div>}
    {list !== null && !err && list.length === 0 && (
      q.trim()
        ? <div className="text-slate-600" data-testid="followers-no-results">{t("followx.no_results")}</div>
        : <div className="text-slate-500" data-testid="followers-empty">{t("followx.empty")}</div>)}
    <div className="space-y-2">
      {(list || []).map((f) => (
        <div key={f.id} className="yp-card p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="follower-row">
          <span className="flex items-center gap-3 min-w-0">
            <Avatar url={f.avatar_url} label={f.nickname} cls="w-10 h-10 text-sm" />
            <span className="font-medium truncate">{f.nickname}</span>
          </span>
          <span className="flex items-center gap-2 flex-wrap sm:shrink-0">
            <Link to={`/chat/${f.id}`} className="yp-btn yp-btn-outline min-h-[44px] !px-3 inline-flex items-center gap-1" data-testid="follower-chat-btn"><MessageCircle size={16} aria-hidden="true" /> {t("followx.chat")}</Link>
            <Link to={`${bioBase}/${f.id}`} className="yp-btn yp-btn-outline min-h-[44px] !px-3 inline-flex items-center" data-testid="follower-bio-btn">{bioLabel}</Link>
            {clinical && <Link to={`/clinica/${f.id}`} className="yp-btn yp-btn-outline min-h-[44px] !px-3 inline-flex items-center" data-testid="follower-clinical-btn">{t("pd.fascicolo")}</Link>}
          </span>
        </div>))}
    </div>
  </div></Layout>);
}
